import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { BRAND } from "@/data/content";

const EASE = [0.76, 0, 0.24, 1];

export default function Preloader() {
  const [count, setCount] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const start = performance.now();
    const duration = 1700;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 2);
      setCount(Math.round(eased * 100));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setTimeout(() => setDone(true), 260);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (done) document.body.style.overflow = "";
  }, [done]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="preloader"
          className="fixed inset-0 z-[200] grain overflow-hidden bg-[var(--bg)]"
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="glow-field" style={{ opacity: 0.7 }} />

          <div className="relative h-full w-full mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col justify-between py-10">
            {/* top row */}
            <motion.div
              className="flex items-center justify-between"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
            >
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 rounded-full bg-[var(--ink)] text-[#0a0707]">
                  <Plus className="h-4 w-4" strokeWidth={2.4} />
                </span>
                <span className="text-[15px] font-medium tracking-tight text-[var(--ink)]">
                  {BRAND.creator}
                </span>
              </div>
              <span className="label">{BRAND.role}</span>
            </motion.div>

            {/* center wordmark */}
            <div className="overflow-hidden">
              <motion.h1
                className="display text-[3.25rem] leading-[0.95] sm:text-[6rem] lg:text-[7.5rem] text-[var(--ink)]"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
              >
                Design that <span className="serif-italic">connects.</span>
              </motion.h1>
            </div>

            {/* bottom row: progress + counter */}
            <div>
              <motion.div
                className="flex items-end justify-between gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span className="label max-w-[16rem] leading-relaxed">
                  Creative · Communications · Web
                </span>
                <span
                  className="display text-[2.5rem] sm:text-[3.5rem] text-[var(--ink)] tabular-nums leading-none"
                  data-testid="preloader-count"
                >
                  {String(count).padStart(3, "0")}
                  <span className="text-[var(--accent)]">%</span>
                </span>
              </motion.div>
              <div className="mt-5 h-[2px] w-full bg-[rgba(245,241,238,0.12)] overflow-hidden rounded-full">
                <div
                  className="h-full bg-[var(--accent)] origin-left"
                  style={{ transform: `scaleX(${count / 100})`, transition: "transform 0.1s linear" }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
